/**
 * In-Memory Backend - For Testing
 *
 * Simple in-memory storage backend for unit tests.
 * Not suitable for production use.
 */
/**
 * InMemoryBackend - Simple Map-based storage
 */
export class InMemoryBackend {
    constructor() {
        this.data = new Map();
    }
    /**
     * Create composite key from namespace, userId, and key
     */
    makeKey(namespace, userId, key) {
        return `${namespace}::${userId}::${key}`;
    }
    /**
     * Parse composite key back to components
     */
    parseKey(compositeKey) {
        const [namespace, userId, key] = compositeKey.split('::');
        return { namespace, userId, key };
    }
    async put(namespace, userId, key, value) {
        const compositeKey = this.makeKey(namespace, userId, key);
        this.data.set(compositeKey, value);
    }
    async get(namespace, userId, key) {
        const compositeKey = this.makeKey(namespace, userId, key);
        const value = this.data.get(compositeKey);
        return value !== undefined ? value : null;
    }
    async delete(namespace, userId, key) {
        const compositeKey = this.makeKey(namespace, userId, key);
        return this.data.delete(compositeKey);
    }
    async list(namespace, userId, limit, offset) {
        const prefix = `${namespace}::${userId}::`;
        const items = [];
        for (const [compositeKey, value] of this.data.entries()) {
            if (compositeKey.startsWith(prefix)) {
                items.push(value);
            }
        }
        return items.slice(offset, offset + limit);
    }
    async exists(namespace, userId, key) {
        const compositeKey = this.makeKey(namespace, userId, key);
        return this.data.has(compositeKey);
    }
    async getStats(namespace, userId) {
        const prefix = `${namespace}::${userId}::`;
        let count = 0;
        for (const compositeKey of this.data.keys()) {
            if (compositeKey.startsWith(prefix)) {
                count++;
            }
        }
        return {
            namespace,
            count,
        };
    }
    async clear(namespace, userId) {
        if (!namespace && !userId) {
            this.data.clear();
            return;
        }
        const keysToDelete = [];
        for (const compositeKey of this.data.keys()) {
            const parsed = this.parseKey(compositeKey);
            if (namespace && userId) {
                if (parsed.namespace === namespace && parsed.userId === userId) {
                    keysToDelete.push(compositeKey);
                }
            }
            else if (namespace) {
                if (parsed.namespace === namespace) {
                    keysToDelete.push(compositeKey);
                }
            }
            else if (userId) {
                if (parsed.userId === userId) {
                    keysToDelete.push(compositeKey);
                }
            }
        }
        for (const key of keysToDelete) {
            this.data.delete(key);
        }
    }
    async close() {
        // No-op for in-memory backend
    }
}
